import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import tokenStore from '../../utils/tokenStore';
import { API_URL } from '../../constants/api';
import AuthShowcasePanel from './AuthShowcasePanel';
import GoogleSignInButton from './GoogleSignInButton';
import Wordmark from '../brand/Wordmark';
import './LoginForm.css';

const API = `${API_URL}/api`;
const GOOGLE_ENABLED = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;
const MOBILE_QUERY = '(max-width: 760px)';

const isMobileViewport = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(MOBILE_QUERY).matches;

const LoginForm = ({ onLogin, switchToRegister, switchToForgotPassword }) => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // On mobile the sign-in sheet is open from the first paint — tapping "Sign In"
  // on the landing page should land straight on the form, not on the product
  // preview interstitial. The preview stays behind it (and is reachable by
  // dismissing the sheet).
  const [sheetOpen, setSheetOpen]       = useState(isMobileViewport);
  // Skip the slide-up animation + auto-focus for that initial auto-open so it
  // reads as "the page is the form", not as a sheet that just appeared.
  const autoOpenedRef = useRef(sheetOpen);
  const sheetEmailRef = useRef(null);

  // Lock body scroll while sheet is open
  useEffect(() => {
    document.body.style.overflow = sheetOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sheetOpen]);

  // Close the sheet if the viewport grows past mobile — the desktop layout
  // hides it, and leaving it "open" would keep body scroll locked.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = (e) => {
      if (!e.matches) {
        autoOpenedRef.current = false;
        setSheetOpen(false);
      }
    };
    mql.addEventListener?.('change', handler);
    return () => mql.removeEventListener?.('change', handler);
  }, []);

  // Auto-focus email when sheet finishes sliding in
  useEffect(() => {
    if (!sheetOpen || autoOpenedRef.current) return;
    const t = setTimeout(() => sheetEmailRef.current?.focus(), 370);
    return () => clearTimeout(t);
  }, [sheetOpen]);

  const openSheet = () => {
    autoOpenedRef.current = false;
    setSheetOpen(true);
  };

  const closeSheet = () => {
    autoOpenedRef.current = false;
    setSheetOpen(false);
  };

  // Close sheet on Escape
  useEffect(() => {
    if (!sheetOpen) return;
    const handler = (e) => { if (e.key === 'Escape') closeSheet(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sheetOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    setError('');
    try {
      const response = await axios({
        method: 'POST',
        url: `${API}/auth/login`,
        data: { email: formData.email.trim(), password: formData.password.trim() },
        timeout: 10000,
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.data.access_token) {
        tokenStore.set(response.data.access_token);
        try {
          const userResponse = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${response.data.access_token}` },
          });
          onLogin(userResponse.data);
        } catch {
          onLogin();
        }
      } else {
        throw new Error('No access token received');
      }
    } catch (err) {
      let msg = 'Login failed';
      if (err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK') {
        msg = 'Network error \u2014 cannot connect to server';
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      } else if (err.message) {
        msg = err.message;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Shared form body — used in both desktop card and mobile sheet
  const formBody = (emailInputRef) => (
    <>
      {error && (
        <div className="fp-login-error" role="alert">
          <span className="fp-login-error-icon" aria-hidden="true">!</span>
          <span>{error}</span>
        </div>
      )}

      {GOOGLE_ENABLED && (
        <>
          <GoogleSignInButton mode="signin" onSuccess={onLogin} disabled={loading} />
          <div className="fp-login-divider" aria-hidden="true">
            <span>or</span>
          </div>
        </>
      )}

      <form onSubmit={handleSubmit} className="fp-login-form" noValidate>
        <div className="fp-login-field">
          <label htmlFor={emailInputRef ? 'fp-sheet-email' : 'fp-login-email'} className="fp-login-label">
            Email
          </label>
          <div className="fp-login-input-wrap">
            <input
              ref={emailInputRef || null}
              id={emailInputRef ? 'fp-sheet-email' : 'fp-login-email'}
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              placeholder="you@company.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="fp-login-input"
              disabled={loading}
              required
            />
          </div>
        </div>

        <div className="fp-login-field">
          <label htmlFor={emailInputRef ? 'fp-sheet-pw' : 'fp-login-password'} className="fp-login-label">
            Password
          </label>
          <div className="fp-login-input-wrap">
            <input
              id={emailInputRef ? 'fp-sheet-pw' : 'fp-login-password'}
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="fp-login-input fp-login-input--password"
              disabled={loading}
              required
            />
            <button
              type="button"
              className="fp-login-pw-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <div className="fp-login-row-end">
          <button type="button" onClick={switchToForgotPassword} className="fp-login-link">
            Forgot password?
          </button>
        </div>

        <button type="submit" disabled={loading} className="fp-login-submit">
          {loading ? (
            <>
              <span className="fp-login-spinner" aria-hidden="true" />
              <span>Signing in...</span>
            </>
          ) : (
            'Sign in'
          )}
        </button>
      </form>

      <div className="fp-login-footer">
        <p className="fp-login-footer-text">
          New to Founders+?{' '}
          <button type="button" onClick={switchToRegister} className="fp-login-footer-cta">
            Join the community
          </button>
        </p>
      </div>
    </>
  );

  return (
    <div className="auth-entry-shell" data-theme="light">
      <div className="auth-entry-grid">
        <AuthShowcasePanel />

        {/* ── Desktop form (hidden on mobile) ── */}
        <div className="auth-entry-form-wrap fp-desktop-form">
          <div className="fp-login auth-entry-card">
            <div className="fp-login-brand">
              <Wordmark
                plusColor="#1A9B8E"
                style={{ height: '26px', width: 'auto', color: '#2C3E50' }}
              />
              <h1 className="fp-login-title">Welcome back</h1>
              <p className="fp-login-subtitle">Sign in to continue building.</p>
            </div>
            {formBody(null)}
          </div>
        </div>
      </div>

      {/* ── Mobile CTA bar (hidden on desktop) ── */}
      <div className="fp-mobile-cta" aria-hidden={sheetOpen ? 'true' : 'false'} style={{ visibility: 'visible', opacity: 1 }}>
        <div className="fp-mobile-cta-inner">
          <div className="fp-mobile-cta-brand">
            <div className="fp-mobile-cta-text">
              <strong className="fp-mobile-cta-name">Founders+</strong>
              <span className="fp-mobile-cta-tagline">The OS for ambitious founders</span>
            </div>
          </div>
          <div className="fp-mobile-cta-actions">
            <button
              className="fp-mobile-cta-btn fp-mobile-cta-btn--primary"
              onClick={openSheet}
            >
              Sign In
            </button>
            <button
              className="fp-mobile-cta-btn fp-mobile-cta-btn--ghost"
              onClick={switchToRegister}
            >
              Join Free
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile bottom sheet (hidden on desktop) ── */}
      <div
        className={`fp-mobile-sheet${sheetOpen ? ' is-open' : ''}${
          autoOpenedRef.current ? ' is-instant' : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Sign in"
      >
        <div
          className="fp-mobile-sheet-backdrop"
          onClick={closeSheet}
          aria-hidden="true"
        />
        <div className="fp-mobile-sheet-panel">
          <div className="fp-mobile-sheet-handle" aria-hidden="true" />
          <div className="fp-login-brand fp-sheet-brand">
            <Wordmark
                plusColor="#1A9B8E"
                style={{ height: '26px', width: 'auto', color: '#2C3E50' }}
              />
            <h2 className="fp-login-title">Welcome back</h2>
            <p className="fp-login-subtitle">Sign in to continue building.</p>
          </div>
          {formBody(sheetEmailRef)}
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
